import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';

const routes: RouteRecordRaw[] = [
    {
        path: '/',
        name: 'home',
        component: () => import( '@/views/HomeView.vue' ),
        meta: {
            title: 'Build with HTML5 - Assessment Platform'
        }
    },
    {
        path: '/assessment/:difficulty/:id',
        name: 'assessment',
        component: () => import( '@/views/AssessmentView.vue' ),
        meta: {
            title: 'Assessment'
        },
        props: true
    },
    {
        path: '/results',
        name: 'results',
        component: () => import( '@/views/ResultsHistoryView.vue' ),
        meta: {
            title: 'Assessment Results History'
        }
    },
    {
        path: '/assessment-result/:resultRecordId',
        name: 'assessment-result',
        component: () => import( '@/views/ResultsView.vue' ),
        props: true,
        meta: {
            title: 'Assessment Results'
        }
    },
    {
        path: '/settings',
        name: 'settings',
        component: () => import( '@/views/SettingsView.vue' ),
        meta: {
            title: 'User Settings'
        }
    },
    {
        // Catch-all 404 route
        path: '/:pathMatch(.*)*',
        redirect: '/'
    }
];

const router = createRouter( {
    history: createWebHistory(),
    routes
} );

// Note: Navigation guards (beforeEach) are now registered in main.ts
// after the router is installed with app.use(router) to ensure they fire properly.

// Update document title on route change
router.beforeEach( async ( to, _from, next ) => {
    const metaTitle = to.meta.title as string | undefined;

    // Build document title using the global store's displayDomainName when available.
    try {
        const { useGlobalStore } = await import( '@/stores/globalStore' );
        const globalStore = useGlobalStore();
        const domainName = String( globalStore.displayDomainName || '' ).trim();

        if ( to.name === 'home' ) {
            if ( domainName ) {
                document.title = `Build with HTML5 - ${domainName} Assessment Platform`;
            } else if ( metaTitle ) {
                document.title = metaTitle;
            }
        } else if ( metaTitle ) {
            if ( to.name === 'settings' ) {
                // Settings should use the plain meta title (no domain prepend)
                document.title = metaTitle;
            } else if ( domainName ) {
                document.title = `${domainName} - ${metaTitle}`;
            } else {
                document.title = metaTitle;
            }
        }

        // Update or create the meta description tag to include the domainName when available.
        try {
            const defaultDescription = 'Learn the programming language or technology of your choice from novice to expert level using multiple-choice assessments and coding challenges.';
            const description = domainName ? `Learn ${domainName} from novice to expert level using multiple-choice assessments and coding challenges.` : defaultDescription;

            let meta = document.querySelector( 'meta[name="description"]' ) as HTMLMetaElement | null;
            if ( meta ) {
                meta.content = description;
            } else {
                meta = document.createElement( 'meta' );
                meta.name = 'description';
                meta.content = description;
                document.head.appendChild( meta );
            }
        } catch ( _e ) {
            // ignore: running in non-browser environment (SSR / tests)
        }
    } catch ( _e ) {
        // If store can't be loaded, fall back to the meta title only.
        if ( metaTitle ) document.title = metaTitle;
    }

    next();
} );

// After navigation completes, coordinate with the uiStore so navigation-initiated
// loading overlays can be dismissed at the right moment. We import the store
// lazily here (inside the hook) so Pinia is guaranteed to be installed by the
// time the hook runs.
router.afterEach( ( to ) => {
    // Dynamically import the ui store so this file can be evaluated during
    // module initialization without requiring Pinia to be installed yet.
    import( '@/stores/uiStore' )
        .then( ( mod ) => {
            try {
                const ui = mod.useUiStore();
                if ( ui.navigationTarget ) {
                    ui.hideLoading();
                    try {
                        if ( to && to.name && String( to.name ) === String( ui.navigationTarget ) ) {
                            ui.setSuppressNextHomeLoading( true );
                        }
                    } catch ( e ) {
                        // ignore
                    }
                }
            } catch ( e ) {
                // ignore
            }
        } )
        .catch( ( e ) => {
            // eslint-disable-next-line no-console
            console.debug( 'router.afterEach: failed to import uiStore', e );
        } );
} );

export default router;
